import { z } from 'zod';

export const ERROR_CODES = [
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  'FORBIDDEN_WORKSPACE',
  'FORBIDDEN_FEATURE',
  'INVALID_REQUEST',
  'INVALID_PROMPT',
  'INVALID_IMAGE',
  'OAUTH_STATE_MISMATCH',
  'OAUTH_PROVIDER_ERROR',
  'CONNECTION_ALREADY_EXISTS',
  'CONNECTION_REVOKED_UPSTREAM',
  'GENERATION_FAILED',
  'GENERATION_TIMEOUT',
  'GENERATION_UPSTREAM_5XX',
  'GENERATION_UPSTREAM_4XX',
  'GENERATION_NOT_FOUND',
  'INSUFFICIENT_CREDITS',
  'CREDIT_HOLD_FAILED',
  'BILLING_PROVIDER_ERROR',
  'ADAPTER_UPSTREAM_4XX',
  'ADAPTER_UPSTREAM_5XX',
  'ADAPTER_RATE_LIMITED',
  'MEDIA_TOO_LARGE',
  'PRODUCT_NOT_FOUND',
  'INTERNAL_ERROR',
  'SERVICE_UNAVAILABLE',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ErrorCodeSchema = z.enum(ERROR_CODES);

export const ErrorBodySchema = z.object({
  code: ErrorCodeSchema,
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

export type ErrorBody = z.infer<typeof ErrorBodySchema>;

export const DEFAULT_ERROR_STATUS: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  SESSION_EXPIRED: 401,
  FORBIDDEN_WORKSPACE: 403,
  FORBIDDEN_FEATURE: 403,
  INVALID_REQUEST: 400,
  INVALID_PROMPT: 422,
  INVALID_IMAGE: 422,
  OAUTH_STATE_MISMATCH: 400,
  OAUTH_PROVIDER_ERROR: 502,
  CONNECTION_ALREADY_EXISTS: 409,
  CONNECTION_REVOKED_UPSTREAM: 401,
  GENERATION_FAILED: 200,
  GENERATION_TIMEOUT: 504,
  GENERATION_UPSTREAM_5XX: 502,
  GENERATION_UPSTREAM_4XX: 422,
  GENERATION_NOT_FOUND: 404,
  INSUFFICIENT_CREDITS: 402,
  CREDIT_HOLD_FAILED: 402,
  BILLING_PROVIDER_ERROR: 502,
  ADAPTER_UPSTREAM_4XX: 422,
  ADAPTER_UPSTREAM_5XX: 502,
  ADAPTER_RATE_LIMITED: 429,
  MEDIA_TOO_LARGE: 413,
  PRODUCT_NOT_FOUND: 404,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

export const DEFAULT_ERROR_MESSAGE: Record<ErrorCode, string> = {
  UNAUTHENTICATED: 'Authentication required.',
  SESSION_EXPIRED: 'Session expired.',
  FORBIDDEN_WORKSPACE: 'No access to this workspace.',
  FORBIDDEN_FEATURE: 'Feature not available on the current plan.',
  INVALID_REQUEST: 'Request body failed validation.',
  INVALID_PROMPT: 'Prompt rejected by safety filter.',
  INVALID_IMAGE: 'Input image rejected.',
  OAUTH_STATE_MISMATCH: 'OAuth state mismatch; restart the connect flow.',
  OAUTH_PROVIDER_ERROR: 'Upstream OAuth provider error.',
  CONNECTION_ALREADY_EXISTS: 'A connection with these identifiers already exists.',
  CONNECTION_REVOKED_UPSTREAM: 'The platform side revoked this connection.',
  GENERATION_FAILED: 'Generation failed.',
  GENERATION_TIMEOUT: 'Generation upstream call timed out.',
  GENERATION_UPSTREAM_5XX: 'Generation upstream returned a 5xx.',
  GENERATION_UPSTREAM_4XX: 'Generation upstream rejected the request.',
  GENERATION_NOT_FOUND: 'Generation job not found.',
  INSUFFICIENT_CREDITS: 'Not enough credits for this task.',
  CREDIT_HOLD_FAILED: 'Could not place credit hold; please retry.',
  BILLING_PROVIDER_ERROR: 'Billing provider error.',
  ADAPTER_UPSTREAM_4XX: 'Platform rejected the request.',
  ADAPTER_UPSTREAM_5XX: 'Platform upstream returned a 5xx.',
  ADAPTER_RATE_LIMITED: 'Platform rate limit hit.',
  MEDIA_TOO_LARGE: 'Asset exceeds platform max bytes or dimensions.',
  PRODUCT_NOT_FOUND: 'Product not found.',
  INTERNAL_ERROR: 'Internal server error.',
  SERVICE_UNAVAILABLE: 'Service temporarily unavailable.',
};
